/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { HtmlHeadInclude, IAny, IGeneric } from '@msdyn365-commerce/core';
import * as React from 'react';

import { ICustomProductPageSummaryData } from './custom-product-page-summary.data';
import { ICustomProductPageSummaryProps } from './custom-product-page-summary.props.autogenerated';

/**
 *
 * ProductPageSummary component.
 * @extends {React.PureComponent<ICustomProductPageSummaryProps>}
 */
class ProductPageSummary extends React.PureComponent<ICustomProductPageSummaryProps<ICustomProductPageSummaryData>> {
    public render(): JSX.Element {
        if (this.props.data.productPageSummary.result === undefined) {
            throw new Error('PageSummaryData input to page-summary is undefined. Unable to generate page-summary');
        }
        let {
            data: {
                productPageSummary: {
                    result: { title, description, keywords, sharingImageUrl, faviconUrl }
                }
            }
        } = this.props;

        // Additional data actions (if present) will be available under props.data
        // The action may either expose additional payloads as separate dataActions
        // (this.props.data.productAvailabilities)
        const productAvailabilities = (this.props.data.productAvailabilities && this.props.data.productAvailabilities.result?.[0]) || null;

        const {
            config,
            context,
            data: {
                productPageSummary: {
                    result: { canonicalUrl, productId, productBrand, price }
                }
            }
        } = this.props;

        // Product SEO metadata specific information - non-overridable
        const productName = title;
        const productDescription = description;
        const productImageUrl = sharingImageUrl;

        // Override values coming from data action if config values are provided
        title = config.title || title;
        description = config.description || description;
        keywords = config.keywords || keywords;
        sharingImageUrl = (config.sharingImage && config.sharingImage.src) || sharingImageUrl;
        faviconUrl = config.faviconUrl || faviconUrl;

        // Construct page title with suffix and prefix if provided from app settings
        const pageTitle = this._constructPageTitle(title, context && context.request && context.request.app);
        return (
            <>
                <HtmlHeadInclude>
                    {pageTitle && <title>{pageTitle}</title>}
                    {description && <meta name='description' content={description} />}
                    {keywords && <meta name='keywords' content={keywords} />}
                    {canonicalUrl && <link rel='canonical' href={canonicalUrl} />}
                    {faviconUrl && <link rel='shortcut icon' href={faviconUrl} />}
                </HtmlHeadInclude>
                {this._renderProductMetadata(
                    productName,
                    productDescription,
                    productImageUrl,
                    canonicalUrl,
                    productId,
                    price,
                    context.request.channel?.Currency,
                    productBrand,
                    productAvailabilities
                )}
                {!config.disableOgTags && this._renderOgTags(pageTitle, description, sharingImageUrl)}
                {!config.disableTwitterTags && this._renderTwitterTags(pageTitle, description, sharingImageUrl)}
            </>
        );
    }

    /**
     * Renders the facebook and other social media specific metadata tags.
     *
     * @param title Page title.
     * @param description Product description.
     * @param sharingImageUrl Primary product image url.
     */
    private _renderOgTags(title: string | undefined, description: string | undefined, sharingImageUrl: string | undefined): JSX.Element {
        return (
            <HtmlHeadInclude>
                {title && <meta property='og:title' content={title} />}
                {description && <meta property='og:description' content={description} />}
                {sharingImageUrl && <meta property='og:image' content={sharingImageUrl} />}
                <meta property='og:type' content='website' />
            </HtmlHeadInclude>
        );
    }

    /**
     * Constructs and renders the JSON-LD tag used to output product specific metadata used by search engine crawlers.
     *
     * @param productName Product name.
     * @param description Product description.
     * @param sharingImageUrl Primary product image url.
     * @param canonicalUrl Product canonical url.
     * @param productId Master product id.
     * @param price Proudct price.
     * @param currencyCode Current channel currency code.
     * @param productBrand Product brand.
     */
    private _renderProductMetadata(
        productName: string | undefined,
        description: string | undefined,
        sharingImageUrl: string | undefined,
        canonicalUrl: string | undefined,
        productId: number | undefined,
        price: number | undefined,
        currencyCode: string | undefined,
        productBrand: string | undefined,
        productAvailabilities: Record<string, string | number> | null
    ): JSX.Element {
        // Map inventory level codes to schema.org availability URIs
        const mapAvailabilityLevel = (): string | undefined => {
            const code = (
                productAvailabilities?.TotalAvailableInventoryLevelCode ||
                productAvailabilities?.PhysicalAvailableInventoryLevelCode ||
                productAvailabilities?.TotalAvailableInventoryLevelLabel
            )?.toString();
            switch ((code || '').toUpperCase()) {
                case 'AVAIL':
                case 'AVAILABLE':
                case 'INSTOCK':
                    return 'https://schema.org/InStock';
                case 'LOW':
                case 'LIMITED':
                    return 'https://schema.org/LimitedAvailability';
                case 'OOS':
                case 'NOTAVAIL':
                case 'OUTOFSTOCK':
                    return 'https://schema.org/OutOfStock';
                default:
                    return undefined;
            }
        };

        // Construct the JSON-LD data that contains the product metadata information used by search enginge crawlers
        const productMetaData = JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: productName,
            description,
            image: sharingImageUrl,
            sku: productId?.toString(),
            brand: {
                '@type': 'Brand',
                name: productBrand || 'Venchi'
            },
            offers: {
                '@type': 'Offer',
                url: canonicalUrl,
                priceCurrency: currencyCode,
                price: price?.toFixed(2),
                availability: mapAvailabilityLevel() || undefined
            }
        });
        return (
            <HtmlHeadInclude>
                {/* eslint-disable-next-line react/no-danger -- Implementation is a copy.*/}
                <script data-load-point='headEnd' type='application/ld+json' dangerouslySetInnerHTML={{ __html: productMetaData }} />
            </HtmlHeadInclude>
        );
    }

    /**
     * Renders the twitter specific metadata tags.
     * @param title Page title.
     * @param description Product description.
     * @param sharingImageUrl Primary product image url.
     */
    private _renderTwitterTags(
        title: string | undefined,
        description: string | undefined,
        sharingImageUrl: string | undefined
    ): JSX.Element {
        return (
            <HtmlHeadInclude>
                {title && <meta name='twitter:title' content={title} />}
                {description && <meta name='twitter:description' content={description} />}
                {sharingImageUrl && <meta name='twitter:image' content={sharingImageUrl} />}
                <meta name='twitter:card' content='summary' />
            </HtmlHeadInclude>
        );
    }

    /**
     * Constructs the page title using the prefix and suffix as defined in app settings and the product name.
     * @param title The product name.
     * @param app App settings.
     */
    private _constructPageTitle(title: string | undefined, app: IGeneric<IAny>): string | undefined {
        if (!title) {
            title = '';
        }
        const moduleConfig = this.props.config;
        if (moduleConfig && moduleConfig.ignoreTitleSuffixPrefix) {
            return title;
        }
        const appConfig = app && app.platform;
        if (appConfig) {
            if (appConfig.pageTitlePrefix) {
                title = `${appConfig.pageTitlePrefix}${title}`;
            }
            if (appConfig.pageTitleSuffix) {
                title = `${title}${appConfig.pageTitleSuffix}`;
            }
        }

        return title;
    }
}

export default ProductPageSummary;
